// Shared cart-related helpers used by Stimulus controllers

// Safely parse JSON; returns null on error
export function safeParse(str) {
  try {
    return JSON.parse(str);
  } catch (_) {
    return null;
  }
}

// Persist to localStorage with basic safety (stringify + try/catch)
export function persistToLocalStorage(key, value) {
  try {
    localStorage.setItem(String(key), JSON.stringify(value));
  } catch (_) {
    // Ignore quota or access errors
  }
}

// Persist a plain string value to localStorage (no JSON stringify)
export function persistString(key, value) {
  try {
    localStorage.setItem(String(key), String(value));
  } catch (_) {
    // Ignore quota or access errors
  }
}

// Normalize arrays of objects by whitelisting allowed fields
export function normalizeForStorage(items, fields) {
  const arr = Array.isArray(items) ? items : [];
  return arr.map((obj) => {
    const out = {};
    for (const k of fields) out[k] = obj?.[k] ?? null;
    return out;
  });
}

// Format currency from cents (USD default)
export function formatCurrency(cents) {
  const amount = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(amount);
  } catch (_) {
    return `$${amount.toFixed(2)}`;
  }
}

// Generate a simple, deterministic ETag-like fingerprint for the cart.
// - Normalizes items: { price: string, quantity: integer > 0 }
// - Sorts by price for stability
// - Encodes as "price:qty|price:qty" and applies a lightweight FNV-1a hash
// - Returns a short hex string (prefixed with 'c')
export function computeCartETag(cart) {
  const normalized = (Array.isArray(cart) ? cart : [])
    .map((i) => ({ price: String(i?.price || ""), quantity: Number(i?.quantity || 0) | 0 }))
    .filter((i) => i.price && i.quantity > 0)
    .sort((a, b) => a.price.localeCompare(b.price));

  const base = normalized.map((i) => `${i.price}:${i.quantity}`).join("|");

  // Early return stable empty marker if no items
  if (!base) return "c0";

  // FNV-1a 32-bit hash
  let hash = 0x811c9dc5; // offset basis
  for (let i = 0; i < base.length; i++) {
    hash ^= base.charCodeAt(i);
    // Multiply by FNV prime (0x01000193), keep as uint32
    hash = (hash >>> 0) * 0x01000193;
  }
  return `c${(hash >>> 0).toString(16)}`;
}
